import { useState, useEffect } from 'react';

const useAssignments = ({ isAuthenticated, authenticatedAxios, conversations, threadId, showToast, setIsPopupVisible }) => {
  // Assignment-related state
  const [assignablePeople, setAssignablePeople] = useState([]);
  const [customerAssignments, setCustomerAssignments] = useState({});
  const [isAssignSubmenuVisible, setIsAssignSubmenuVisible] = useState(false);
  const [isAddPersonModalVisible, setIsAddPersonModalVisible] = useState(false);
  const [newPersonName, setNewPersonName] = useState('');
  const [newPersonEmail, setNewPersonEmail] = useState('');
  const [newPersonRole, setNewPersonRole] = useState('');
  const [isAddingPerson, setIsAddingPerson] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  // Fetch assignable people on mount
  useEffect(() => {
    let cancelled = false;
    const fetchAssignablePeople = async () => {
      try {
        const response = await authenticatedAxios.get('/api/assignments/people');
        if (!cancelled && response.data?.people) {
          setAssignablePeople(response.data.people);
        }
      } catch (error) {
        console.error('Error fetching assignable people:', error);
      }
    };
    
    if (isAuthenticated) {
      fetchAssignablePeople();
    }
    return () => { cancelled = true; };
  }, [isAuthenticated, authenticatedAxios]);

  // Fetch assignments when conversations change
  useEffect(() => {
    let cancelled = false;
    const fetchAssignments = async () => {
      if (!conversations || conversations.length === 0) return;
      
      try {
        const threadIds = conversations.map(c => c.threadId);
        const response = await authenticatedAxios.post('/api/assignments/by-threads', { threadIds });
        if (!cancelled && response.data?.assignmentMap) {
          setCustomerAssignments(response.data.assignmentMap);
        }
      } catch (error) {
        console.error('Error fetching assignments:', error);
      }
    };
    
    if (isAuthenticated && conversations.length > 0) {
      fetchAssignments();
    }
    return () => { cancelled = true; };
  }, [isAuthenticated, conversations, authenticatedAxios]);

  // Assign customer to a person
  const handleAssignCustomer = async (personId, personName) => {
    if (isAssigning || !threadId) return;
    
    setIsAssigning(true);
    try {
      // Find the conversation to get customer info
      const conversation = conversations.find(c => c.threadId === threadId);
      if (!conversation) {
        console.error('Conversation not found');
        return;
      }
      
      const response = await authenticatedAxios.post('/api/assignments/assign', {
        customerId: conversation.id,
        customerName: conversation.name,
        customerEmail: conversation.email,
        customerPhone: conversation.phone || conversation.phoneNumber || conversation.userPhone,
        threadId: threadId,
        assignToPersonId: personId
      });
      
      if (response.data?.assignment) {
        // Update local state
        setCustomerAssignments(prev => ({
          ...prev,
          [threadId]: response.data.assignment
        }));
        
        // Close menus
        setIsAssignSubmenuVisible(false);
        setIsPopupVisible(false);
        
        // Show success message (you could add a toast here)
        console.log(`Customer assigned to ${personName}`);
      }
    } catch (error) {
      console.error('Error assigning customer:', error);
    } finally {
      setIsAssigning(false);
    }
  };

  // Add new assignable person (with optional assignment context)
  const handleAddPerson = async () => {
    if (!newPersonName.trim() || !newPersonEmail.trim() || isAddingPerson) return;
    
    setIsAddingPerson(true);
    try {
      // Find the current conversation for context
      const currentConversation = conversations.find(c => c.threadId === threadId);
      
      const requestData = {
        name: newPersonName.trim(),
        email: newPersonEmail.trim(),
        role: newPersonRole.trim() || undefined
      };
      
      // If we have a conversation context, include it for combined invitation+assignment
      if (currentConversation && threadId) {
        requestData.customerName = currentConversation.name;
        requestData.customerEmail = currentConversation.email;
        requestData.threadId = threadId;
      }
      
      const response = await authenticatedAxios.post('/api/assignments/people', requestData);
      
      if (response.data?.person) {
        setAssignablePeople(prev => [...prev, response.data.person]);
        
        // If we assigned, update local assignment state
        if (currentConversation && threadId) {
          setCustomerAssignments(prev => ({
            ...prev,
            [threadId]: {
              assignedToId: response.data.person._id,
              assignedToName: response.data.person.name,
              assignedToEmail: response.data.person.email,
              status: 'active'
            }
          }));
          showToast(`${newPersonName} invited and assigned to ${currentConversation.name}`, 'success');
        } else {
          showToast(`Invitation sent to ${newPersonEmail}`, 'success');
        }
        
        // Reset form
        setNewPersonName('');
        setNewPersonEmail('');
        setNewPersonRole('');
        setIsAddPersonModalVisible(false);
      }
    } catch (error) {
      console.error('Error adding person:', error);
      showToast(error.response?.data?.error || 'Failed to add person', 'error');
    } finally {
      setIsAddingPerson(false);
    }
  };

  // Get assignment for a thread
  const getAssignmentForThread = (threadId) => {
    return customerAssignments[threadId];
  };

  return {
    assignablePeople,
    setAssignablePeople,
    customerAssignments,
    setCustomerAssignments,
    isAssignSubmenuVisible,
    setIsAssignSubmenuVisible,
    isAddPersonModalVisible,
    setIsAddPersonModalVisible,
    newPersonName,
    setNewPersonName,
    newPersonEmail,
    setNewPersonEmail,
    newPersonRole,
    setNewPersonRole,
    isAddingPerson,
    setIsAddingPerson,
    isAssigning,
    setIsAssigning,
    handleAssignCustomer,
    handleAddPerson,
    getAssignmentForThread,
  };
};

export default useAssignments;
